/**
 * How a date reads on screen.
 *
 * Three surfaces run a chronology — the thoughts feed, its day columns, and
 * the plan's recent list — and a date that reads `fri 1 aug` in one and
 * `Aug 1` in another says the two are unrelated things.
 */

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** `fri 1 aug` */
export function dayLabel(at: Date): string {
  return `${DAYS[at.getDay()]} ${at.getDate()} ${MONTHS[at.getMonth()]}`;
}

/** `aug 2026` */
export function monthLabel(at: Date): string {
  return `${MONTHS[at.getMonth()]} ${at.getFullYear()}`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The italic head over a day's run, in words: `Today`, `Yesterday`, then
 * `Tuesday, 22 September`. A day in another year says its year.
 */
export function dayHead(at: Date, now: Date): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const back = Math.round((day(now) - day(at)) / 86_400_000);
  if (back === 0) return 'Today';
  if (back === 1) return 'Yesterday';
  const year = at.getFullYear() !== now.getFullYear() ? ` ${at.getFullYear()}` : '';
  return `${WEEKDAYS[at.getDay()]}, ${at.getDate()} ${MONTH_NAMES[at.getMonth()]}${year}`;
}

/** The head over a month further back: `August 2026`. */
export function monthHead(at: Date): string {
  return `${MONTH_NAMES[at.getMonth()]} ${at.getFullYear()}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A day in a margin: `23 Sep`, with its year when it is not this one. */
export function shortDate(at: Date, now: Date = new Date()): string {
  const year = at.getFullYear() !== now.getFullYear() ? ` ${at.getFullYear()}` : '';
  return `${at.getDate()} ${SHORT_MONTHS[at.getMonth()]}${year}`;
}

/** A moment in a margin: `23 Sep · 20:23`. */
export function shortWhen(at: Date, now: Date = new Date()): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `${shortDate(at, now)} · ${two(at.getHours())}:${two(at.getMinutes())}`;
}
